/**
 * ComparisonDashboard.js
 * Side-by-side comparison of raw vs cleaned data quality metrics
 * Executive-friendly visualization showing cleaning impact
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../utils/ThemeSwitcher';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  BarChart3,
  Copy,
  Calendar,
  GitBranch,
  MapPin,
  Database,
  Trash2,
  ArrowRight,
  ArrowDown,
  RefreshCw,
  Award,
  Target,
  Loader,
  Info,
  // Ship 4: icons for the additional detectors
  MessageSquare,   // sentinel phrases
  Link as LinkIcon,// gated fields
  Activity,        // straight-lining
  Code2,           // encoding mojibake
  Shuffle,         // backcheck discrepancies
  Scissors,        // dropped cols / cleanup ops
  Tag,             // flag columns added
  Wand2,           // standardize / cleaning script glue
} from 'lucide-react';
import AILoadingOverlay from '../common/AILoadingOverlay';

const ComparisonDashboard = ({
  projectId,
  rawCheckResults = {},
  cleanedCheckResults = {},
  onRefresh = null,
  selectedChecks = []
}) => {
  const { isDarkMode } = useTheme();
  const [comparisonData, setComparisonData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('comparison');

  // Responsive
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = windowWidth < 768;

  const API_BASE = process.env.REACT_APP_API_URL || 'https://api.dataquadapp.com';

  // Helper: determine if a check should be displayed
  const isCheckActive = useCallback((checkId) => {
    if (!selectedChecks || selectedChecks.length === 0) return true; // show all if none specified
    return selectedChecks.includes(checkId);
  }, [selectedChecks]);

  const getAuthHeaders = useCallback(() => {
    let userEmail = '';
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      userEmail = userInfo.email || '';
    } catch (e) {
      userEmail = localStorage.getItem('userEmail') || '';
    }
    return {
      'user_email': userEmail,
      'User-Email': userEmail,
      'Content-Type': 'application/json'
    };
  }, []);

  // Fetch comparison data from backend
  const fetchComparisonData = useCallback(async () => {
    if (!projectId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(
        `${API_BASE}/api/cleaning/comparison/${projectId}`,
        { headers: getAuthHeaders() }
      );
      
      const data = await response.json();
      
      if (data.success) {
        setComparisonData(data.comparison);
      } else {
        setError(data.detail || 'Failed to fetch comparison data');
      }
    } catch (err) {
      setError(`Error fetching comparison: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [projectId, API_BASE, getAuthHeaders]);

  useEffect(() => {
    fetchComparisonData();
  }, [fetchComparisonData]);

  // Calculate quality metrics from check results
  // Ship 4: extended to all 13 detectors. Returned object also carries a
  // _measured Set listing which detectors actually ran on this side. Display
  // code can read `metrics._measured.has('detector_id')` to render
  // "Not measured" instead of a misleading 0 for the cleaned side.
  const calculateMetrics = (checkResults) => {
    if (!checkResults || Object.keys(checkResults).length === 0) {
      return null;
    }

    // Helper: did this detector run on this side?
    const measured = new Set();
    const recordMeasured = (detectorKey) => {
      const r = checkResults[detectorKey];
      if (!r) return;
      if (r._not_run_on_cleaned) return;
      if (r.overall && r.overall._not_run_on_cleaned) return;
      measured.add(detectorKey);
    };

    const metrics = {
      duplicates: 0,
      missingPct: 0,
      outliers: 0,
      durationIssues: 0,
      surveyFlowIssues: 0,
      geolocationIssues: 0,
      similarPairs: 0,
      textOutliers: 0,
      // Ship 4: coaching-loop + misc detectors
      sentinelFlags: 0,
      gatedFlags: 0,
      probeFlags: 0,
      straightLining: 0,
      encodingIssues: 0,
      backcheckIssues: 0,
      _measured: measured,  // Set of detector IDs that ran on this side
    };

    // =====================================================
    // DUPLICATES - Handle various nested structures
    // =====================================================
    if (checkResults.duplicates) {
      const dup = checkResults.duplicates;
      
      // Try direct fields first
      if (dup.total_duplicates !== undefined) {
        metrics.duplicates = dup.total_duplicates;
      } else if (dup.duplicate_count !== undefined) {
        metrics.duplicates = dup.duplicate_count;
      } else if (dup.total_duplicate_values !== undefined) {
        metrics.duplicates = dup.total_duplicate_values;
      }
      // Try nested in summary
      else if (dup.summary?.total_duplicates !== undefined) {
        metrics.duplicates = dup.summary.total_duplicates;
      }
      // Try nested in overall
      else if (dup.overall?.total_duplicates !== undefined) {
        metrics.duplicates = dup.overall.total_duplicates;
      }
      // Sum from per_variable structure
      else if (dup.per_variable || dup.variables || dup.overall?.per_variable) {
        const vars = dup.per_variable || dup.variables || dup.overall?.per_variable || {};
        metrics.duplicates = Object.values(vars).reduce((sum, v) => {
          return sum + (v.duplicate_count || v.count || v.duplicates || 0);
        }, 0);
      }
      // Sum from columns structure
      else if (dup.columns) {
        metrics.duplicates = Object.values(dup.columns).reduce((sum, v) => {
          return sum + (v.duplicate_count || v.count || 0);
        }, 0);
      }
      recordMeasured('duplicates');
    }

    // =====================================================
    // MISSINGNESS - Handle nested overall.per_variable structure
    // =====================================================
    if (checkResults.missingness) {
      const miss = checkResults.missingness;
      
      // Try direct fields
      if (miss.overall_missing_rate !== undefined) {
        metrics.missingPct = miss.overall_missing_rate;
      } else if (miss.overall_missing_pct !== undefined) {
        metrics.missingPct = miss.overall_missing_pct;
      } else if (miss.total_missing_pct !== undefined) {
        metrics.missingPct = miss.total_missing_pct;
      }
      // Try nested in summary
      else if (miss.summary?.overall_missing_pct !== undefined) {
        metrics.missingPct = miss.summary.overall_missing_pct;
      }
      // Calculate from overall.per_variable structure
      else if (miss.overall?.per_variable) {
        const vars = miss.overall.per_variable;
        let totalMissing = 0;
        let totalCells = 0;
        let maxMissingPct = 0;
        Object.values(vars).forEach(v => {
          if (v.missing_count !== undefined && v.total_count !== undefined) {
            totalMissing += v.missing_count;
            totalCells += v.total_count;
          }
          if (v.missing_percentage !== undefined) {
            maxMissingPct = Math.max(maxMissingPct, v.missing_percentage);
          }
        });
        if (totalCells > 0) {
          metrics.missingPct = (totalMissing / totalCells) * 100;
        } else if (maxMissingPct > 0) {
          metrics.missingPct = maxMissingPct;
        }
      }
      // Calculate from per_variable at root level
      else if (miss.per_variable) {
        const vars = miss.per_variable;
        let totalMissing = 0;
        let totalCells = 0;
        Object.values(vars).forEach(v => {
          if (v.missing_count !== undefined && v.total_count !== undefined) {
            totalMissing += v.missing_count;
            totalCells += v.total_count;
          }
        });
        if (totalCells > 0) {
          metrics.missingPct = (totalMissing / totalCells) * 100;
        }
      }
      // Variables directly at root level of miss.overall
      else if (miss.overall) {
        let totalMissing = 0;
        let totalCells = 0;
        let maxMissingPct = 0;
        Object.entries(miss.overall).forEach(([key, v]) => {
          if (key.startsWith('_') || key === 'timeline_data' || key === 'has_timeline' || 
              key === 'total_observations' || key === 'by_collector') {
            return;
          }
          if (v && typeof v === 'object') {
            if (v.missing !== undefined && v.total !== undefined) {
              totalMissing += v.missing;
              totalCells += v.total;
            }
            if (v.missing_percentage !== undefined) {
              maxMissingPct = Math.max(maxMissingPct, v.missing_percentage);
            }
          }
        });
        if (totalCells > 0) {
          metrics.missingPct = (totalMissing / totalCells) * 100;
        } else if (maxMissingPct > 0) {
          metrics.missingPct = maxMissingPct;
        }
      }
      recordMeasured('missingness');
    }

    // =====================================================
    // OUTLIERS - Handle various nested structures
    // =====================================================
    if (checkResults.outliers) {
      const out = checkResults.outliers;
      
      if (out.total_outliers !== undefined) {
        metrics.outliers = out.total_outliers;
      } else if (out.outlier_count !== undefined) {
        metrics.outliers = out.outlier_count;
      } else if (out.total_outlier_count !== undefined) {
        metrics.outliers = out.total_outlier_count;
      }
      else if (out.summary?.total_outliers !== undefined) {
        metrics.outliers = out.summary.total_outliers;
      }
      else if (out.overall?.total_outliers !== undefined) {
        metrics.outliers = out.overall.total_outliers;
      }
      else if (out.per_variable || out.variables || out.overall?.per_variable) {
        const vars = out.per_variable || out.variables || out.overall?.per_variable || {};
        metrics.outliers = Object.values(vars).reduce((sum, v) => {
          return sum + (v.outlier_count || v.count || v.outliers || v.n_outliers || 0);
        }, 0);
      }
      else if (out.columns) {
        metrics.outliers = Object.values(out.columns).reduce((sum, v) => {
          return sum + (v.outlier_count || v.count || v.n_outliers || 0);
        }, 0);
      }
      else if (out.overall) {
        let totalOutliers = 0;
        Object.entries(out.overall).forEach(([key, value]) => {
          if (key.startsWith('_') || key === 'by_collector' || key === 'timeline_data' || key === 'has_timeline') {
            return;
          }
          if (value && typeof value === 'object' && 'outlier_count' in value) {
            totalOutliers += value.outlier_count || 0;
          }
        });
        if (totalOutliers > 0) {
          metrics.outliers = totalOutliers;
        }
      }
      recordMeasured('outliers');
    }

    // =====================================================
    // DURATION - Handle various nested structures
    // =====================================================
    if (checkResults.duration) {
      const dur = checkResults.duration;
      
      const shortCount = dur.too_short_count || dur.short_count || dur.short || 0;
      const longCount = dur.too_long_count || dur.long_count || dur.long || 0;
      
      if (shortCount > 0 || longCount > 0) {
        metrics.durationIssues = shortCount + longCount;
      }
      else if (dur.total_flagged !== undefined) {
        metrics.durationIssues = dur.total_flagged;
      } else if (dur.flagged_count !== undefined) {
        metrics.durationIssues = dur.flagged_count;
      } else if (dur.outlier_count !== undefined) {
        metrics.durationIssues = dur.outlier_count;
      } else if (dur.total_issues !== undefined) {
        metrics.durationIssues = dur.total_issues;
      }
      else if (dur.summary) {
        metrics.durationIssues = (dur.summary.too_short_count || 0) + (dur.summary.too_long_count || 0) ||
                                 dur.summary.total_flagged || dur.summary.flagged_count || 
                                 dur.summary.outlier_count || 0;
      }
      else if (dur.overall) {
        if (dur.overall.outlier_count !== undefined) {
          metrics.durationIssues = dur.overall.outlier_count;
        } else {
          metrics.durationIssues = (dur.overall.too_short_count || 0) + (dur.overall.too_long_count || 0) ||
                                   dur.overall.total_flagged || 0;
        }
      }
      recordMeasured('duration');
    }

    // =====================================================
    // SURVEY FLOW
    // =====================================================
    if (checkResults.survey_flow) {
      const flow = checkResults.survey_flow;
      const flowOverall = flow.overall || flow;
      metrics.surveyFlowIssues = flowOverall.total_issues || 
                                 flowOverall.issues_count ||
                                 flowOverall.flagged_count ||
                                 flowOverall.summary?.total_issues ||
                                 flow.total_issues ||
                                 0;
      recordMeasured('survey_flow');
    }

    // =====================================================
    // GEOLOCATION
    // =====================================================
    if (checkResults.geolocation) {
      const geo = checkResults.geolocation;
      const geoOverall = geo.overall || geo;
      metrics.geolocationIssues = geoOverall.flagged_count || 
                                  geoOverall.issues_count ||
                                  geoOverall.total_flagged ||
                                  geoOverall.summary?.flagged_count ||
                                  geo.flagged_count ||
                                  0;
      recordMeasured('geolocation');
    }

    // =====================================================
    // TEXT CLUSTERING - Similar pairs and text outliers
    // =====================================================
    if (checkResults.text_clustering) {
      const text = checkResults.text_clustering;
      const textOverall = text.overall || text;
      metrics.similarPairs =
        textOverall.total_similar_pairs ??
        textOverall.similar_pairs_count ??
        text.total_similar_pairs ??
        text.similar_pairs_count ??
        0;
      metrics.textOutliers =
        textOverall.total_outliers ??
        textOverall.outlier_count ??
        text.total_outliers ??
        text.outlier_count ??
        0;
      recordMeasured('text_clustering');
    }

    // =====================================================
    // SENTINEL PHRASES (Ship 4)
    // Cleaning's sentinel branch already blanks these in-place, so a much
    // lower count after cleaning is the EXPECTED outcome.
    // =====================================================
    if (checkResults.sentinel_phrases) {
      const sent = checkResults.sentinel_phrases;
      const sentOverall = sent.overall || sent;
      metrics.sentinelFlags =
        (Array.isArray(sentOverall.flagged_rows) ? sentOverall.flagged_rows.length : 0) ||
        sentOverall.total_flagged ||
        sentOverall.flagged_count ||
        0;
      recordMeasured('sentinel_phrases');

      // Encoding is a sub-flag of sentinel: count rows with flag_type='encoding'
      if (Array.isArray(sentOverall.flagged_rows)) {
        const enc = sentOverall.flagged_rows.filter(r => r?.flag_type === 'encoding').length;
        if (enc > 0) {
          metrics.encodingIssues = enc;
          measured.add('encoding');
        }
      }
    }
    // Standalone encoding result (if separate from sentinel)
    if (checkResults.encoding && !measured.has('encoding')) {
      const enc = checkResults.encoding;
      const encOverall = enc.overall || enc;
      metrics.encodingIssues =
        encOverall.flagged_count ||
        encOverall.total_flagged ||
        encOverall.encoding_issues_count ||
        0;
      recordMeasured('encoding');
    }

    // =====================================================
    // GATED FIELDS (Ship 4)
    // =====================================================
    if (checkResults.gated_fields) {
      const gate = checkResults.gated_fields;
      const gateOverall = gate.overall || gate;
      metrics.gatedFlags =
        (Array.isArray(gateOverall.flagged_rows) ? gateOverall.flagged_rows.length : 0) ||
        gateOverall.total_flagged ||
        gateOverall.flagged_count ||
        0;
      recordMeasured('gated_fields');
    }

    // =====================================================
    // PROBE DEPTH (Ship 4)
    // =====================================================
    if (checkResults.probe_depth) {
      const probe = checkResults.probe_depth;
      const probeOverall = probe.overall || probe;
      metrics.probeFlags =
        (Array.isArray(probeOverall.flagged_rows) ? probeOverall.flagged_rows.length : 0) ||
        probeOverall.total_flagged ||
        probeOverall.flagged_count ||
        0;
      recordMeasured('probe_depth');
    }

    // =====================================================
    // STRAIGHT-LINING (Ship 4)
    // =====================================================
    if (checkResults.straight_lining) {
      const sl = checkResults.straight_lining;
      const slOverall = sl.overall || sl;
      metrics.straightLining =
        (Array.isArray(slOverall.flagged_rows) ? slOverall.flagged_rows.length : 0) ||
        slOverall.total_flagged ||
        slOverall.flagged_count ||
        slOverall.respondents_flagged ||
        0;
      recordMeasured('straight_lining');
    }

    // =====================================================
    // BACKCHECK (Ship 4) — typically not_run_on_cleaned
    // =====================================================
    if (checkResults.backcheck) {
      const bc = checkResults.backcheck;
      const bcOverall = bc.overall || bc;
      metrics.backcheckIssues =
        bcOverall.total_discrepancies ||
        bcOverall.discrepancy_count ||
        bc.total_discrepancies ||
        bc.discrepancy_count ||
        0;
      recordMeasured('backcheck');
    }

    return metrics;
  };

  const rawMetrics = calculateMetrics(rawCheckResults);
  const cleanedMetrics = calculateMetrics(cleanedCheckResults);

  // Calculate grade based on metrics (only for selected and MEASURED checks)
  // Ship 4: extended to all 13 detectors. Skipping a check that wasn't run on
  // this side (metrics._measured.has(...) === false) is critical — otherwise
  // the cleaned grade gets an undeserved boost from "0 sentinel phrases" when
  // really we just didn't measure them.
  const calculateGrade = (metrics) => {
    if (!metrics) return { grade: 'N/A', score: 0, color: '#6B7280' };

    const measured = metrics._measured || new Set();
    const wasRun = (id) => measured.has(id);

    let score = 100;

    // Original 4
    if (isCheckActive('duplicates') && wasRun('duplicates') && metrics.duplicates > 0)
      score -= Math.min(15, metrics.duplicates * 0.5);
    if (isCheckActive('missingness') && wasRun('missingness') && metrics.missingPct > 5)
      score -= Math.min(20, metrics.missingPct);
    if (isCheckActive('outliers') && wasRun('outliers') && metrics.outliers > 50)
      score -= Math.min(15, (metrics.outliers - 50) * 0.1);
    if (isCheckActive('duration') && wasRun('duration') && metrics.durationIssues > 10)
      score -= Math.min(15, metrics.durationIssues * 0.5);

    // Ship 4: new detectors. Weights chosen to be additive but not dominant —
    // a perfectly clean dataset on the original 4 with serious sentinel/gated
    // issues should still take a noticeable hit, but no single new detector
    // can crater the score by itself.
    if (isCheckActive('survey_flow') && wasRun('survey_flow') && metrics.surveyFlowIssues > 0)
      score -= Math.min(10, metrics.surveyFlowIssues * 0.3);
    if (isCheckActive('geolocation') && wasRun('geolocation') && metrics.geolocationIssues > 0)
      score -= Math.min(10, metrics.geolocationIssues * 0.5);
    if (isCheckActive('text_clustering') && wasRun('text_clustering')) {
      if (metrics.similarPairs > 5)
        score -= Math.min(8, (metrics.similarPairs - 5) * 0.4);
      if (metrics.textOutliers > 10)
        score -= Math.min(5, (metrics.textOutliers - 10) * 0.2);
    }
    if (isCheckActive('sentinel_phrases') && wasRun('sentinel_phrases') && metrics.sentinelFlags > 0)
      score -= Math.min(10, metrics.sentinelFlags * 0.4);
    if (isCheckActive('gated_fields') && wasRun('gated_fields') && metrics.gatedFlags > 0)
      score -= Math.min(10, metrics.gatedFlags * 0.5);
    if (isCheckActive('probe_depth') && wasRun('probe_depth') && metrics.probeFlags > 5)
      score -= Math.min(8, (metrics.probeFlags - 5) * 0.3);
    if (isCheckActive('straight_lining') && wasRun('straight_lining') && metrics.straightLining > 0)
      score -= Math.min(12, metrics.straightLining * 1.0);  // serious — whole respondents
    if (isCheckActive('backcheck') && wasRun('backcheck') && metrics.backcheckIssues > 0)
      score -= Math.min(10, metrics.backcheckIssues * 0.4);

    score = Math.max(0, Math.round(score));

    let grade, color;
    if (score >= 90) { grade = 'A'; color = '#10b9abff'; }
    else if (score >= 80) { grade = 'B'; color = '#10b3b9ff'; }
    else if (score >= 70) { grade = 'C'; color = '#FBBF24'; }
    else if (score >= 60) { grade = 'D'; color = '#F59E0B'; }
    else { grade = 'F'; color = '#EF4444'; }
    return { grade, score, color };
  };

  const rawGrade = calculateGrade(rawMetrics);
  const cleanedGrade = calculateGrade(cleanedMetrics);

  // Styles
  const styles = {
    container: {
      padding: isMobile ? '16px 12px' : '24px 32px',
      backgroundColor: isDarkMode ? '#0D0E0E' : '#F5F5F599',
      minHeight: '100vh',
      fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
    },
    header: {
      marginBottom: '16px',
    },
    title: {
      fontSize: isMobile ? '20px' : '22px',
      fontWeight: '500',
      color: isDarkMode ? '#f0ede4' : '#1a1a1a',
      marginBottom: '4px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      letterSpacing: '-0.02em',
    },
    subtitle: {
      fontSize: '12px',
      color: isDarkMode ? '#a8a49a' : '#555550',
      opacity: 0.8,
    },
    viewToggle: {
      display: 'inline-flex',
      gap: '2px',
      marginBottom: '16px',
      backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.055)' : 'rgba(0, 0, 0, 0.045)',
      border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.07)'}`,
      padding: '3px',
      borderRadius: '11px',
      width: 'fit-content',
      maxWidth: '100%',
      overflowX: 'auto',
      flexWrap: 'nowrap',
    },
    toggleButton: {
      padding: '6px 13px',
      borderRadius: '8px',
      border: 'none',
      fontSize: '12.5px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.15s ease',
      whiteSpace: 'nowrap',
      letterSpacing: '-0.005em',
      fontFamily: 'inherit',
      flexShrink: 0,
    },
    comparisonGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '1fr auto 1fr',
      gap: isMobile ? '16px' : '20px',
      marginBottom: '20px',
    },
    dataCard: {
      backgroundColor: isDarkMode ? '#111114' : '#FFFFFF',
      borderRadius: '8px',
      padding: '18px 20px',
      border: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)'}`,
    },
    cardTitle: {
      fontSize: '13px',
      fontWeight: '500',
      color: isDarkMode ? '#f0ede4' : '#1a1a1a',
      marginBottom: '18px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      letterSpacing: '-0.01em',
    },
    gradeCircle: {
      width: '90px',
      height: '90px',
      borderRadius: '50%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '8px auto 20px',
    },
    gradeLabel: {
      fontSize: '32px',
      fontWeight: '600',
      color: '#FFFFFF',
      letterSpacing: '-0.02em',
      lineHeight: 1,
    },
    scoreLabel: {
      fontSize: '11px',
      color: 'rgba(255, 255, 255, 0.85)',
      letterSpacing: '0.02em',
      marginTop: '2px',
      fontVariantNumeric: 'tabular-nums',
    },
    metricRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '10px 0',
      borderBottom: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`,
    },
    metricLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '12px',
      color: isDarkMode ? '#a8a49a' : '#6B7280',
    },
    metricValue: {
      fontSize: '13px',
      fontWeight: '500',
      color: isDarkMode ? '#E5E7EB' : '#222222',
      fontVariantNumeric: 'tabular-nums',
      letterSpacing: '-0.005em',
    },
    arrowColumn: {
      display: 'flex',
      flexDirection: isMobile ? 'row' : 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: isMobile ? '0' : '20px 0',
    },
    changeIndicator: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '4px 12px',
      borderRadius: '100px',
      fontSize: '11px',
      fontWeight: '500',
      marginBottom: '12px',
      border: '0.5px solid transparent',
      letterSpacing: '-0.005em',
    },
    summaryCard: {
      backgroundColor: isDarkMode ? '#111114' : '#FFFFFF',
      borderRadius: '8px',
      padding: '18px 20px',
      border: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)'}`,
      marginBottom: '16px',
    },
    summaryGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '12px',
    },
    summaryItem: {
      padding: '16px 18px',
      backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
      border: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`,
      borderRadius: '6px',
      textAlign: 'center',
    },
    summaryValue: {
      fontSize: '24px',
      fontWeight: '600',
      marginBottom: '4px',
      letterSpacing: '-0.02em',
      fontVariantNumeric: 'tabular-nums',
    },
    summaryLabel: {
      fontSize: '11px',
      color: isDarkMode ? '#a8a49a' : '#6B7280',
      letterSpacing: '0.02em',
    },
    issuesCard: {
      backgroundColor: isDarkMode ? '#111114' : '#FFFFFF',
      borderRadius: '8px',
      padding: '18px 20px',
      border: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)'}`,
    },
    issueItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '10px 12px',
      backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
      border: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`,
      borderRadius: '6px',
      marginBottom: '6px',
    },
    loadingContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: isMobile ? '24px' : '60px',
      color: isDarkMode ? '#a8a49a' : '#6B7280',
    },
    errorBanner: {
      backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.08)' : 'rgba(239, 68, 68, 0.06)',
      color: isDarkMode ? '#F87171' : '#991b1b',
      padding: '10px 14px',
      borderRadius: '6px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      border: `0.5px solid ${isDarkMode ? 'rgba(239, 68, 68, 0.25)' : 'rgba(239, 68, 68, 0.2)'}`,
      marginBottom: '12px',
      fontSize: '12px',
    },
  };

  // Render metric change indicator
  const renderChange = (rawValue, cleanedValue, isPercentage = false, lowerIsBetter = true) => {
    if (rawValue === undefined || cleanedValue === undefined) return null;
    
    const diff = cleanedValue - rawValue;
    const isImproved = lowerIsBetter ? diff < 0 : diff > 0;
    const isUnchanged = diff === 0;
    
    const color = isUnchanged 
      ? (isDarkMode ? '#6B7280' : '#9CA3AF')
      : isImproved 
        ? '#10B981' 
        : '#EF4444';
    
    const Icon = isUnchanged ? Minus : isImproved ? TrendingDown : TrendingUp;
    
    return (
      <span style={{ 
        display: 'inline-flex', 
        alignItems: 'center', 
        gap: '4px',
        color,
        fontSize: '13px',
        fontWeight: '500'
      }}>
        <Icon size={14} />
        {isUnchanged ? 'No change' : `${diff > 0 ? '+' : ''}${isPercentage ? diff.toFixed(1) + '%' : diff}`}
      </span>
    );
  };

  // Ship 4 helpers ────────────────────────────────────────────────────────
  // Has this detector been measured on the given side? Reads metrics._measured.
  const wasMeasured = (metrics, detectorId) =>
    !!(metrics && metrics._measured && metrics._measured.has(detectorId));

  // "Not measured" pill for the cleaned side when backend skipped a detector.
  const notMeasuredBadge = (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '2px 8px',
      borderRadius: '100px',
      fontSize: '10px',
      fontWeight: 500,
      letterSpacing: '0.02em',
      color: isDarkMode ? '#a8a49a' : '#6B7280',
      backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.03)',
      border: `0.5px dashed ${isDarkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)'}`,
      whiteSpace: 'nowrap'
    }}>
      Not measured
    </span>
  );

  // Render a row in the RAW card (left side of side-by-side). Hides entirely
  // when the detector isn't active AND has no data anywhere — keeps the card
  // uncluttered for users running just a few detectors.
  const RawCardRow = ({ icon: Icon, label, detectorId, value, isPercent = false }) => {
    const rawMeasured     = wasMeasured(rawMetrics, detectorId);
    const cleanedMeasured = wasMeasured(cleanedMetrics, detectorId);
    const isActive        = isCheckActive(detectorId);
    if (!isActive && !rawMeasured && !cleanedMeasured) return null;
    const fmt = (v) => (isPercent ? `${(v || 0).toFixed(1)}%` : (v || 0).toLocaleString());
    return (
      <div style={styles.metricRow}>
        <span style={styles.metricLabel}>
          <Icon size={13} /> {label}
        </span>
        <span style={styles.metricValue}>
          {rawMeasured ? fmt(value) : <span style={{ opacity: 0.5 }}>—</span>}
        </span>
      </div>
    );
  };

  // Render a row in the CLEANED card (right side). Shows cleaned value + a
  // change indicator vs raw, OR a "Not measured" badge if backend skipped this
  // detector on the cleaned side.
  const CleanedCardRow = ({ icon: Icon, label, detectorId, rawValue, cleanedValue, isPercent = false }) => {
    const rawMeasured     = wasMeasured(rawMetrics, detectorId);
    const cleanedMeasured = wasMeasured(cleanedMetrics, detectorId);
    const isActive        = isCheckActive(detectorId);
    if (!isActive && !rawMeasured && !cleanedMeasured) return null;
    const fmt = (v) => (isPercent ? `${(v || 0).toFixed(1)}%` : (v || 0).toLocaleString());
    return (
      <div style={styles.metricRow}>
        <span style={styles.metricLabel}>
          <Icon size={13} /> {label}
        </span>
        <span style={styles.metricValue}>
          {cleanedMeasured
            ? <>{fmt(cleanedValue)}<span style={{ marginLeft: '8px' }}>{rawMeasured ? renderChange(rawValue, cleanedValue, isPercent) : null}</span></>
            : notMeasuredBadge}
        </span>
      </div>
    );
  };

  // Standalone metric row (raw-only / cleaned-only views). One value, no diff.
  const StandaloneMetricRow = ({ icon: Icon, label, detectorId, value, isMeasured, isPercent = false, comparisonValue, lowerIsBetter = true }) => {
    if (!isMeasured && !value) return null;
    const fmt = (v) => (isPercent ? `${(v || 0).toFixed(1)}%` : (v || 0).toLocaleString());
    let arrow = null;
    if (comparisonValue !== undefined && isMeasured && value !== comparisonValue) {
      const diff = value - comparisonValue;
      const isBetter = lowerIsBetter ? diff < 0 : diff > 0;
      arrow = (
        <span style={{ color: isBetter ? '#10B981' : '#EF4444', fontSize: '13px', marginLeft: '6px' }}>
          {isBetter ? '↓' : '↑'} {Math.abs(diff).toLocaleString()}{isPercent ? '%' : ''}
        </span>
      );
    }
    return (
      <div style={{
        ...styles.metricRow,
        padding: '12px 14px',
        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
        border: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`,
        borderRadius: '6px',
        marginBottom: '6px'
      }}>
        <span style={styles.metricLabel}>
          <Icon size={14} /> {label}
        </span>
        <span style={{ ...styles.metricValue, fontSize: '13px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px' }}>
          {isMeasured ? fmt(value) : notMeasuredBadge}
          {arrow}
        </span>
      </div>
    );
  };

  // Render loading state
  if (isLoading) {
    return (
      <div style={styles.container}>
        <AILoadingOverlay
          isVisible={true}
          context="quality_analysis"
          title="Loading Comparison Data"
          subtitle="Preparing raw and cleaned data quality metrics"
        />
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorBanner}>
          <AlertCircle size={20} />
          {error}
        </div>
      </div>
    );
  }

  // Check if cleaned data exists
  const hasCleanedData = comparisonData?.cleaned !== null || cleanedMetrics !== null;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>
          <Target size={18} style={{ color: isDarkMode ? '#a8a49a' : '#555550', opacity: 0.85 }} />
          Data Quality Comparison
        </h1>
        <p style={styles.subtitle}>
          Compare data quality metrics before and after cleaning to measure improvement
        </p>
      </div>

      {/* View Toggle */}
      <div style={styles.viewToggle}>
        {['comparison', 'raw', 'cleaned'].map((mode) => (
          <button
            key={mode}
            style={{
              ...styles.toggleButton,
              backgroundColor: viewMode === mode
                ? (isDarkMode ? '#17181a' : '#FFFFFF')
                : 'transparent',
              color: viewMode === mode
                ? (isDarkMode ? '#ececec' : '#1a1a1a')
                : (isDarkMode ? '#6d7178' : '#8a8f98'),
              fontWeight: viewMode === mode ? 600 : 500,
              boxShadow: viewMode === mode
                ? (isDarkMode
                    ? '0 1px 2px rgba(0,0,0,0.4)'
                    : '0 1px 2px rgba(0,0,0,0.08), 0 1px 6px rgba(0,0,0,0.06)')
                : 'none',
              opacity: (mode === 'cleaned' && !hasCleanedData) ? 0.4 : 1,
              cursor: (mode === 'cleaned' && !hasCleanedData) ? 'not-allowed' : 'pointer',
            }}
            onClick={() => setViewMode(mode)}
            disabled={mode === 'cleaned' && !hasCleanedData}
          >
            {mode === 'comparison' ? 'Side by Side' : 
             mode === 'raw' ? 'Raw Data Only' : 'Cleaned Data Only'}
          </button>
        ))}
        
        {onRefresh && (
          <button
            style={{
              ...styles.toggleButton,
              backgroundColor: 'transparent',
              color: isDarkMode ? '#a8a49a' : '#6B7280',
              display: 'inline-flex',
              alignItems: 'center',
              padding: '7px 10px',
            }}
            onClick={onRefresh}
            title="Refresh"
          >
            <RefreshCw size={13} />
          </button>
        )}
      </div>

      {/* No Cleaned Data Warning */}
      {!hasCleanedData && viewMode === 'comparison' && (
        <div style={{
          ...styles.errorBanner,
          backgroundColor: isDarkMode ? 'rgba(217, 119, 6, 0.1)' : 'rgba(217, 119, 6, 0.06)',
          color: isDarkMode ? '#FCD34D' : '#92400E',
          border: `0.5px solid ${isDarkMode ? 'rgba(217, 119, 6, 0.25)' : 'rgba(217, 119, 6, 0.2)'}`
        }}>
          <AlertTriangle size={14} />
          <div>
            <strong style={{ fontWeight: 500 }}>No cleaned data available.</strong> Run a cleaning script first to see the comparison.
          </div>
        </div>
      )}

      {/* Summary Stats */}
      {comparisonData && (
        <div style={styles.summaryCard}>
          <h3 style={{ ...styles.cardTitle, marginBottom: '14px' }}>
            <Database size={14} style={{ color: isDarkMode ? '#a8a49a' : '#6B7280' }} />
            Dataset Overview
          </h3>
          <div style={styles.summaryGrid}>
            <div style={styles.summaryItem}>
              <div style={{ ...styles.summaryValue, color: isDarkMode ? '#60A5FA' : '#2563EB' }}>
                {comparisonData.raw?.total_rows?.toLocaleString() || '—'}
              </div>
              <div style={styles.summaryLabel}>Original Rows</div>
            </div>
            
            {comparisonData.cleaned && (
              <>
                <div style={styles.summaryItem}>
                  <div style={{ ...styles.summaryValue, color: '#10B981' }}>
                    {comparisonData.cleaned?.total_rows?.toLocaleString() || '—'}
                  </div>
                  <div style={styles.summaryLabel}>Cleaned Rows</div>
                </div>
                
                <div style={styles.summaryItem}>
                  <div style={{ 
                    ...styles.summaryValue, 
                    color: comparisonData.delta?.rows_removed > 0 ? '#D97706' : '#10B981'
                  }}>
                    {comparisonData.delta?.rows_removed > 0 ? '-' : ''}{comparisonData.delta?.rows_removed?.toLocaleString() || '0'}
                  </div>
                  <div style={styles.summaryLabel}>Rows Removed ({comparisonData.delta?.rows_removed_pct || 0}%)</div>
                </div>
                
                {comparisonData.delta?.score_change !== undefined && (
                  <div style={styles.summaryItem}>
                    <div style={{ 
                      ...styles.summaryValue, 
                      color: comparisonData.delta.score_change >= 0 ? '#10B981' : '#EF4444'
                    }}>
                      {comparisonData.delta.score_change >= 0 ? '+' : ''}{comparisonData.delta.score_change}
                    </div>
                    <div style={styles.summaryLabel}>Quality Score Change</div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Comparison Grid */}
      {viewMode === 'comparison' && (
        <div style={styles.comparisonGrid}>
          {/* Raw Data Card */}
          <div style={styles.dataCard}>
            <h3 style={styles.cardTitle}>
              <Database size={14} style={{ color: isDarkMode ? '#60A5FA' : '#2563EB' }} />
              Raw Data (Before)
            </h3>
            
            <div style={{
              ...styles.gradeCircle,
              background: `linear-gradient(135deg, ${rawGrade.color}, ${rawGrade.color}dd)`
            }}>
              <span style={styles.gradeLabel}>{rawGrade.grade}</span>
              <span style={styles.scoreLabel}>{rawGrade.score}/100</span>
            </div>
            
            {rawMetrics && (
              <div>
                <RawCardRow icon={Copy}          label="Duplicates"        detectorId="duplicates"      value={rawMetrics.duplicates} />
                <RawCardRow icon={BarChart3}     label="Missing Values"    detectorId="missingness"    value={rawMetrics.missingPct} isPercent />
                <RawCardRow icon={AlertTriangle} label="Outliers"          detectorId="outliers"       value={rawMetrics.outliers} />
                <RawCardRow icon={Calendar}      label="Duration Issues"   detectorId="duration"       value={rawMetrics.durationIssues} />
                <RawCardRow icon={GitBranch}     label="Survey Flow"       detectorId="survey_flow"    value={rawMetrics.surveyFlowIssues} />
                <RawCardRow icon={MapPin}        label="GPS Issues"        detectorId="geolocation"    value={rawMetrics.geolocationIssues} />
                <RawCardRow icon={Copy}          label="Similar Pairs"     detectorId="text_clustering" value={rawMetrics.similarPairs} />
                <RawCardRow icon={AlertCircle}   label="Atypical Text"     detectorId="text_clustering" value={rawMetrics.textOutliers} />
                <RawCardRow icon={MessageSquare} label="Sentinel Phrases"  detectorId="sentinel_phrases" value={rawMetrics.sentinelFlags} />
                <RawCardRow icon={LinkIcon}      label="Gated Fields"      detectorId="gated_fields"   value={rawMetrics.gatedFlags} />
                <RawCardRow icon={Target}        label="Probe Depth"       detectorId="probe_depth"    value={rawMetrics.probeFlags} />
                <RawCardRow icon={Activity}      label="Straight-Lining"   detectorId="straight_lining" value={rawMetrics.straightLining} />
                <RawCardRow icon={Code2}         label="Encoding Issues"   detectorId="sentinel_phrases" value={rawMetrics.encodingIssues} />
                <RawCardRow icon={Shuffle}       label="Back-check"        detectorId="backcheck"      value={rawMetrics.backcheckIssues} />
              </div>
            )}
          </div>

          {/* Arrow Column */}
          <div style={styles.arrowColumn}>
            <ArrowRight size={24} style={{ color: isDarkMode ? '#4B5563' : '#9CA3AF', transform: isMobile ? 'rotate(90deg)' : 'none', opacity: 0.6 }} />
            
            {hasCleanedData && cleanedMetrics && rawMetrics && (
              <div style={{ marginTop: '20px', textAlign: 'center' }}>
                {cleanedGrade.score > rawGrade.score && (
                  <div style={{
                    ...styles.changeIndicator,
                    backgroundColor: isDarkMode ? 'rgba(16, 185, 129, 0.1)' : 'rgba(16, 185, 129, 0.06)',
                    color: '#10B981',
                    borderColor: 'rgba(16, 185, 129, 0.3)'
                  }}>
                    <TrendingUp size={13} />
                    Improved!
                  </div>
                )}
                {cleanedGrade.score === rawGrade.score && (
                  <div style={{
                    ...styles.changeIndicator,
                    backgroundColor: 'transparent',
                    color: isDarkMode ? '#a8a49a' : '#6B7280',
                    borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'
                  }}>
                    <Minus size={13} />
                    No Change
                  </div>
                )}
                {cleanedGrade.score < rawGrade.score && (
                  <div style={{
                    ...styles.changeIndicator,
                    backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.06)',
                    color: '#EF4444',
                    borderColor: 'rgba(239, 68, 68, 0.3)'
                  }}>
                    <TrendingDown size={13} />
                    Decreased
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Cleaned Data Card */}
          <div style={{
            ...styles.dataCard,
            opacity: hasCleanedData ? 1 : 0.5
          }}>
            <h3 style={styles.cardTitle}>
              <CheckCircle size={14} style={{ color: '#10B981' }} />
              Cleaned Data (After)
            </h3>
            
            {hasCleanedData && cleanedMetrics ? (
              <>
                <div style={{
                  ...styles.gradeCircle,
                  background: `linear-gradient(135deg, ${cleanedGrade.color}, ${cleanedGrade.color}dd)`
                }}>
                  <span style={styles.gradeLabel}>{cleanedGrade.grade}</span>
                  <span style={styles.scoreLabel}>{cleanedGrade.score}/100</span>
                </div>
                
                <div>
                  <CleanedCardRow icon={Copy}          label="Duplicates"        detectorId="duplicates"      rawValue={rawMetrics?.duplicates}        cleanedValue={cleanedMetrics.duplicates} />
                  <CleanedCardRow icon={BarChart3}     label="Missing Values"    detectorId="missingness"    rawValue={rawMetrics?.missingPct}        cleanedValue={cleanedMetrics.missingPct} isPercent />
                  <CleanedCardRow icon={AlertTriangle} label="Outliers"          detectorId="outliers"       rawValue={rawMetrics?.outliers}          cleanedValue={cleanedMetrics.outliers} />
                  <CleanedCardRow icon={Calendar}      label="Duration Issues"   detectorId="duration"       rawValue={rawMetrics?.durationIssues}    cleanedValue={cleanedMetrics.durationIssues} />
                  <CleanedCardRow icon={GitBranch}     label="Survey Flow"       detectorId="survey_flow"    rawValue={rawMetrics?.surveyFlowIssues}  cleanedValue={cleanedMetrics.surveyFlowIssues} />
                  <CleanedCardRow icon={MapPin}        label="GPS Issues"        detectorId="geolocation"    rawValue={rawMetrics?.geolocationIssues} cleanedValue={cleanedMetrics.geolocationIssues} />
                  <CleanedCardRow icon={Copy}          label="Similar Pairs"     detectorId="text_clustering" rawValue={rawMetrics?.similarPairs}     cleanedValue={cleanedMetrics.similarPairs} />
                  <CleanedCardRow icon={AlertCircle}   label="Atypical Text"     detectorId="text_clustering" rawValue={rawMetrics?.textOutliers}     cleanedValue={cleanedMetrics.textOutliers} />
                  <CleanedCardRow icon={MessageSquare} label="Sentinel Phrases"  detectorId="sentinel_phrases" rawValue={rawMetrics?.sentinelFlags}    cleanedValue={cleanedMetrics.sentinelFlags} />
                  <CleanedCardRow icon={LinkIcon}      label="Gated Fields"      detectorId="gated_fields"   rawValue={rawMetrics?.gatedFlags}        cleanedValue={cleanedMetrics.gatedFlags} />
                  <CleanedCardRow icon={Target}        label="Probe Depth"       detectorId="probe_depth"    rawValue={rawMetrics?.probeFlags}        cleanedValue={cleanedMetrics.probeFlags} />
                  <CleanedCardRow icon={Activity}      label="Straight-Lining"   detectorId="straight_lining" rawValue={rawMetrics?.straightLining}   cleanedValue={cleanedMetrics.straightLining} />
                  <CleanedCardRow icon={Code2}         label="Encoding Issues"   detectorId="sentinel_phrases" rawValue={rawMetrics?.encodingIssues}  cleanedValue={cleanedMetrics.encodingIssues} />
                  <CleanedCardRow icon={Shuffle}       label="Back-check"        detectorId="backcheck"      rawValue={rawMetrics?.backcheckIssues}   cleanedValue={cleanedMetrics.backcheckIssues} />
                </div>
              </>
            ) : (
              <div style={{ 
                textAlign: 'center', 
                padding: '40px 20px',
                color: isDarkMode ? '#6B7280' : '#9CA3AF'
              }}>
                <AlertTriangle size={28} style={{ marginBottom: '14px', opacity: 0.5 }} />
                <p style={{ marginBottom: '6px', fontSize: '13px', fontWeight: 500, color: isDarkMode ? '#a8a49a' : '#6B7280' }}>Quality analysis not run yet</p>
                <p style={{ fontSize: '12px', opacity: 0.75 }}>
                  Click "Run Quality Analysis" button above to analyze cleaned data
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ===================== CLEANING OPERATIONS IMPACT (Ship 4) ===================== */}
      {/* Surfaces what the cleaning script DID — rows dropped, columns dropped,
          flag columns added, fields blanked. Reads comparisonData.column_changes
          and comparisonData.cleaning_operations populated by the backend. */}
      {viewMode === 'comparison' && hasCleanedData && comparisonData?.cleaning_operations && (
        <div style={styles.issuesCard}>
          <h3 style={styles.cardTitle}>
            <Wand2 size={14} style={{ color: isDarkMode ? '#a8a49a' : '#6B7280' }} />
            Cleaning Operations Impact
          </h3>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '12px',
            marginTop: '12px'
          }}>
            {/* Rows dropped */}
            <div style={{
              padding: '14px 16px',
              backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
              border: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`,
              borderRadius: '6px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px', color: isDarkMode ? '#a8a49a' : '#6B7280', fontSize: '11px', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                <Trash2 size={12} /> Rows Dropped
              </div>
              <div style={{ fontSize: '22px', fontWeight: 600, color: comparisonData.cleaning_operations.rows_dropped > 0 ? '#D97706' : '#10B981', letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums' }}>
                {(comparisonData.cleaning_operations.rows_dropped || 0).toLocaleString()}
              </div>
              {comparisonData.delta?.rows_removed_pct !== undefined && (
                <div style={{ fontSize: '11px', color: isDarkMode ? '#8a867d' : '#6B7280', marginTop: '2px' }}>
                  {comparisonData.delta.rows_removed_pct}% of original
                </div>
              )}
            </div>

            {/* Columns dropped */}
            <div style={{
              padding: '14px 16px',
              backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
              border: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`,
              borderRadius: '6px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px', color: isDarkMode ? '#a8a49a' : '#6B7280', fontSize: '11px', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                <Scissors size={12} /> Columns Dropped
              </div>
              <div style={{ fontSize: '22px', fontWeight: 600, color: isDarkMode ? '#f0ede4' : '#1a1a1a', letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums' }}>
                {comparisonData.cleaning_operations.columns_dropped || 0}
              </div>
              {comparisonData.column_changes?.dropped?.length > 0 && (
                <div style={{ fontSize: '11px', color: isDarkMode ? '#8a867d' : '#6B7280', marginTop: '4px', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', wordBreak: 'break-all' }} title={comparisonData.column_changes.dropped.join(', ')}>
                  {comparisonData.column_changes.dropped.slice(0, 3).join(', ')}
                  {comparisonData.column_changes.dropped.length > 3 && ` +${comparisonData.column_changes.dropped.length - 3} more`}
                </div>
              )}
            </div>

            {/* Flag columns added */}
            <div style={{
              padding: '14px 16px',
              backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
              border: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`,
              borderRadius: '6px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px', color: isDarkMode ? '#a8a49a' : '#6B7280', fontSize: '11px', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                <Tag size={12} /> Flag Columns Added
              </div>
              <div style={{ fontSize: '22px', fontWeight: 600, color: isDarkMode ? '#f0ede4' : '#1a1a1a', letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums' }}>
                {comparisonData.cleaning_operations.flag_columns_added || 0}
              </div>
              {comparisonData.column_changes?.flag_columns?.length > 0 && (
                <div style={{ fontSize: '11px', color: isDarkMode ? '#8a867d' : '#6B7280', marginTop: '4px', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' }}>
                  {comparisonData.column_changes.flag_columns.map(fc => {
                    const cnt = comparisonData.cleaning_operations.flag_row_counts?.[fc];
                    return `${fc.replace(/^_flag_/, '')}${cnt !== undefined ? ` (${cnt})` : ''}`;
                  }).join(', ')}
                  {comparisonData.cleaning_operations.flag_row_counts?._sampled && (
                    <span style={{ opacity: 0.6 }}> · sampled</span>
                  )}
                </div>
              )}
            </div>

            {/* Standardize impact (informational — Standardize ops always run) */}
            <div style={{
              padding: '14px 16px',
              backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
              border: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`,
              borderRadius: '6px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px', color: isDarkMode ? '#a8a49a' : '#6B7280', fontSize: '11px', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                <Wand2 size={12} /> Standardize
              </div>
              <div style={{ fontSize: '13px', fontWeight: 500, color: isDarkMode ? '#f0ede4' : '#1a1a1a', letterSpacing: '-0.01em', lineHeight: 1.4 }}>
                Whitespace, NFC unicode,<br />numeric / date coercion
              </div>
              <div style={{ fontSize: '11px', color: isDarkMode ? '#8a867d' : '#6B7280', marginTop: '4px' }}>
                Always applied
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Remaining Issues Section */}
      {viewMode === 'comparison' && hasCleanedData && cleanedMetrics && (
        <div style={styles.issuesCard}>
          <h3 style={styles.cardTitle}>
            <AlertTriangle size={14} style={{ color: '#D97706' }} />
            Remaining Issues After Cleaning
          </h3>

          {/* Ship 4: extended thresholds. Only flag detectors that were
              actually measured — we don't want to crow about "0 sentinel
              phrases" if we never ran sentinel detection on cleaned data. */}
          {(() => {
            const m = cleanedMetrics;
            const wm = (id) => m._measured && m._measured.has(id);
            const issues = [];
            if (wm('duplicates')      && m.duplicates > 0)        issues.push({ id: 'dup',  icon: Copy,          color: '#EF4444', label: <><strong>{m.duplicates} duplicate records</strong> still present</> });
            if (wm('missingness')     && m.missingPct > 2)        issues.push({ id: 'miss', icon: BarChart3,     color: '#D97706', label: <><strong>{m.missingPct.toFixed(1)}% missing values</strong> remain{rawMetrics && ` (was ${rawMetrics.missingPct.toFixed(1)}%)`}</> });
            if (wm('outliers')        && m.outliers > 20)         issues.push({ id: 'out',  icon: AlertTriangle, color: '#D97706', label: <><strong>{m.outliers} outliers</strong> still detected{rawMetrics && m.outliers < rawMetrics.outliers && <span style={{ color: '#10B981', marginLeft: '8px' }}>(was {rawMetrics.outliers})</span>}</> });
            if (wm('duration')        && m.durationIssues > 5)    issues.push({ id: 'dur',  icon: Calendar,      color: '#D97706', label: <><strong>{m.durationIssues} duration issues</strong> remain</> });
            if (wm('survey_flow')     && m.surveyFlowIssues > 0)  issues.push({ id: 'flow', icon: GitBranch,     color: '#D97706', label: <><strong>{m.surveyFlowIssues} survey flow violations</strong> remain</> });
            if (wm('geolocation')     && m.geolocationIssues > 0) issues.push({ id: 'geo',  icon: MapPin,        color: '#D97706', label: <><strong>{m.geolocationIssues} GPS issues</strong> remain</> });
            if (wm('text_clustering') && m.similarPairs > 5)      issues.push({ id: 'sim',  icon: Copy,          color: '#D97706', label: <><strong>{m.similarPairs} similar response pairs</strong> remain</> });
            if (wm('sentinel_phrases')&& m.sentinelFlags > 0)     issues.push({ id: 'sent', icon: MessageSquare, color: '#D97706', label: <><strong>{m.sentinelFlags} sentinel-phrase / short responses</strong> still flagged{rawMetrics && m.sentinelFlags < rawMetrics.sentinelFlags && <span style={{ color: '#10B981', marginLeft: '8px' }}>(was {rawMetrics.sentinelFlags})</span>}</> });
            if (wm('gated_fields')    && m.gatedFlags > 0)        issues.push({ id: 'gate', icon: LinkIcon,      color: '#D97706', label: <><strong>{m.gatedFlags} gated-field contradictions</strong> remain</> });
            if (wm('probe_depth')     && m.probeFlags > 5)        issues.push({ id: 'prob', icon: Target,        color: '#D97706', label: <><strong>{m.probeFlags} low-specificity responses</strong> flagged</> });
            if (wm('straight_lining') && m.straightLining > 0)    issues.push({ id: 'sl',   icon: Activity,      color: '#EF4444', label: <><strong>{m.straightLining} straight-lining respondents</strong> still flagged</> });
            if (wm('encoding')        && m.encodingIssues > 0)    issues.push({ id: 'enc',  icon: Code2,         color: '#D97706', label: <><strong>{m.encodingIssues} encoding (mojibake) issues</strong> remain</> });

            if (issues.length === 0) {
              return (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '16px',
                  backgroundColor: isDarkMode ? 'rgba(16, 185, 129, 0.08)' : 'rgba(16, 185, 129, 0.06)',
                  border: `0.5px solid ${isDarkMode ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.15)'}`,
                  borderRadius: '6px',
                  color: '#10B981'
                }}>
                  <CheckCircle size={24} />
                  <div>
                    <strong>Excellent!</strong> All measured quality issues have been addressed.
                  </div>
                </div>
              );
            }
            return (
              <div>
                {issues.map(({ id, icon: Icon, color, label }) => (
                  <div key={id} style={styles.issueItem}>
                    <Icon size={14} style={{ color }} />
                    <div>{label}</div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {/* ===================== RAW DATA ONLY VIEW ===================== */}
      {viewMode === 'raw' && (
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <div style={styles.dataCard}>
            <h3 style={styles.cardTitle}>
              <Database size={14} style={{ color: isDarkMode ? '#60A5FA' : '#2563EB' }} />
              Raw Data Quality Analysis
            </h3>
            
            <div style={{
              ...styles.gradeCircle,
              background: `linear-gradient(135deg, ${rawGrade.color}, ${rawGrade.color}dd)`,
              width: '140px',
              height: '140px',
              margin: '24px auto'
            }}>
              <span style={{ ...styles.gradeLabel, fontSize: '48px' }}>{rawGrade.grade}</span>
              <span style={{ ...styles.scoreLabel, fontSize: '13px' }}>{rawGrade.score}/100</span>
            </div>
            
            {rawMetrics && (
              <div style={{ marginTop: '24px' }}>
                <StandaloneMetricRow icon={Copy}          label="Duplicate Values"        detectorId="duplicates"        value={rawMetrics.duplicates}        isMeasured={wasMeasured(rawMetrics, 'duplicates')} />
                <StandaloneMetricRow icon={BarChart3}     label="Missing Values"          detectorId="missingness"      value={rawMetrics.missingPct}        isMeasured={wasMeasured(rawMetrics, 'missingness')} isPercent />
                <StandaloneMetricRow icon={AlertTriangle} label="Statistical Outliers"    detectorId="outliers"         value={rawMetrics.outliers}          isMeasured={wasMeasured(rawMetrics, 'outliers')} />
                <StandaloneMetricRow icon={Calendar}      label="Duration Issues"         detectorId="duration"         value={rawMetrics.durationIssues}    isMeasured={wasMeasured(rawMetrics, 'duration')} />
                <StandaloneMetricRow icon={GitBranch}     label="Survey Flow Issues"      detectorId="survey_flow"      value={rawMetrics.surveyFlowIssues}  isMeasured={wasMeasured(rawMetrics, 'survey_flow')} />
                <StandaloneMetricRow icon={MapPin}        label="GPS Issues"              detectorId="geolocation"      value={rawMetrics.geolocationIssues} isMeasured={wasMeasured(rawMetrics, 'geolocation')} />
                <StandaloneMetricRow icon={Copy}          label="Similar Text Pairs"      detectorId="text_clustering"  value={rawMetrics.similarPairs}      isMeasured={wasMeasured(rawMetrics, 'text_clustering')} />
                <StandaloneMetricRow icon={AlertCircle}   label="Atypical Text Responses" detectorId="text_clustering"  value={rawMetrics.textOutliers}      isMeasured={wasMeasured(rawMetrics, 'text_clustering')} />
                <StandaloneMetricRow icon={MessageSquare} label="Sentinel Phrases"        detectorId="sentinel_phrases" value={rawMetrics.sentinelFlags}     isMeasured={wasMeasured(rawMetrics, 'sentinel_phrases')} />
                <StandaloneMetricRow icon={LinkIcon}      label="Gated Field Violations"  detectorId="gated_fields"     value={rawMetrics.gatedFlags}        isMeasured={wasMeasured(rawMetrics, 'gated_fields')} />
                <StandaloneMetricRow icon={Target}        label="Probe Depth (low)"       detectorId="probe_depth"      value={rawMetrics.probeFlags}        isMeasured={wasMeasured(rawMetrics, 'probe_depth')} />
                <StandaloneMetricRow icon={Activity}      label="Straight-Lining"         detectorId="straight_lining"  value={rawMetrics.straightLining}    isMeasured={wasMeasured(rawMetrics, 'straight_lining')} />
                <StandaloneMetricRow icon={Code2}         label="Encoding Issues"         detectorId="sentinel_phrases" value={rawMetrics.encodingIssues}    isMeasured={wasMeasured(rawMetrics, 'encoding') || wasMeasured(rawMetrics, 'sentinel_phrases')} />
                <StandaloneMetricRow icon={Shuffle}       label="Back-check Discrepancies" detectorId="backcheck"       value={rawMetrics.backcheckIssues}   isMeasured={wasMeasured(rawMetrics, 'backcheck')} />
              </div>
            )}
            
            <div style={{ 
              marginTop: '24px', 
              padding: '16px', 
              backgroundColor: isDarkMode ? 'rgba(37, 99, 235, 0.08)' : 'rgba(37, 99, 235, 0.06)',
              border: `0.5px solid ${isDarkMode ? 'rgba(37, 99, 235, 0.2)' : 'rgba(37, 99, 235, 0.15)'}`,
              borderRadius: '6px',
              fontSize: '12px',
              color: isDarkMode ? '#93C5FD' : '#1E40AF'
            }}>
              <strong>Note:</strong> This shows quality metrics from your original dataset before any cleaning operations were applied.
            </div>
          </div>
        </div>
      )}

      {/* ===================== CLEANED DATA ONLY VIEW ===================== */}
      {viewMode === 'cleaned' && (
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <div style={styles.dataCard}>
            <h3 style={styles.cardTitle}>
              <CheckCircle size={18} style={{ color: '#10B981' }} />
              Cleaned Data Quality Analysis
            </h3>
            
            {cleanedMetrics ? (
              <>
                <div style={{
                  ...styles.gradeCircle,
                  background: `linear-gradient(135deg, ${cleanedGrade.color}, ${cleanedGrade.color}dd)`,
                  width: '140px',
                  height: '140px',
                  margin: '24px auto'
                }}>
                  <span style={{ ...styles.gradeLabel, fontSize: '48px' }}>{cleanedGrade.grade}</span>
                  <span style={{ ...styles.scoreLabel, fontSize: '13px' }}>{cleanedGrade.score}/100</span>
                </div>
                
                <div style={{ marginTop: '24px' }}>
                  <StandaloneMetricRow icon={Copy}          label="Duplicate Values"        detectorId="duplicates"        value={cleanedMetrics.duplicates}        isMeasured={wasMeasured(cleanedMetrics, 'duplicates')}      comparisonValue={rawMetrics?.duplicates} />
                  <StandaloneMetricRow icon={BarChart3}     label="Missing Values"          detectorId="missingness"      value={cleanedMetrics.missingPct}        isMeasured={wasMeasured(cleanedMetrics, 'missingness')}    comparisonValue={rawMetrics?.missingPct} isPercent />
                  <StandaloneMetricRow icon={AlertTriangle} label="Statistical Outliers"    detectorId="outliers"         value={cleanedMetrics.outliers}          isMeasured={wasMeasured(cleanedMetrics, 'outliers')}       comparisonValue={rawMetrics?.outliers} />
                  <StandaloneMetricRow icon={Calendar}      label="Duration Issues"         detectorId="duration"         value={cleanedMetrics.durationIssues}    isMeasured={wasMeasured(cleanedMetrics, 'duration')}       comparisonValue={rawMetrics?.durationIssues} />
                  <StandaloneMetricRow icon={GitBranch}     label="Survey Flow Issues"      detectorId="survey_flow"      value={cleanedMetrics.surveyFlowIssues}  isMeasured={wasMeasured(cleanedMetrics, 'survey_flow')}    comparisonValue={rawMetrics?.surveyFlowIssues} />
                  <StandaloneMetricRow icon={MapPin}        label="GPS Issues"              detectorId="geolocation"      value={cleanedMetrics.geolocationIssues} isMeasured={wasMeasured(cleanedMetrics, 'geolocation')}    comparisonValue={rawMetrics?.geolocationIssues} />
                  <StandaloneMetricRow icon={Copy}          label="Similar Text Pairs"      detectorId="text_clustering"  value={cleanedMetrics.similarPairs}      isMeasured={wasMeasured(cleanedMetrics, 'text_clustering')} comparisonValue={rawMetrics?.similarPairs} />
                  <StandaloneMetricRow icon={AlertCircle}   label="Atypical Text Responses" detectorId="text_clustering"  value={cleanedMetrics.textOutliers}      isMeasured={wasMeasured(cleanedMetrics, 'text_clustering')} comparisonValue={rawMetrics?.textOutliers} />
                  <StandaloneMetricRow icon={MessageSquare} label="Sentinel Phrases"        detectorId="sentinel_phrases" value={cleanedMetrics.sentinelFlags}     isMeasured={wasMeasured(cleanedMetrics, 'sentinel_phrases')} comparisonValue={rawMetrics?.sentinelFlags} />
                  <StandaloneMetricRow icon={LinkIcon}      label="Gated Field Violations"  detectorId="gated_fields"     value={cleanedMetrics.gatedFlags}        isMeasured={wasMeasured(cleanedMetrics, 'gated_fields')}   comparisonValue={rawMetrics?.gatedFlags} />
                  <StandaloneMetricRow icon={Target}        label="Probe Depth (low)"       detectorId="probe_depth"      value={cleanedMetrics.probeFlags}        isMeasured={wasMeasured(cleanedMetrics, 'probe_depth')}    comparisonValue={rawMetrics?.probeFlags} />
                  <StandaloneMetricRow icon={Activity}      label="Straight-Lining"         detectorId="straight_lining"  value={cleanedMetrics.straightLining}    isMeasured={wasMeasured(cleanedMetrics, 'straight_lining')} comparisonValue={rawMetrics?.straightLining} />
                  <StandaloneMetricRow icon={Code2}         label="Encoding Issues"         detectorId="sentinel_phrases" value={cleanedMetrics.encodingIssues}    isMeasured={wasMeasured(cleanedMetrics, 'encoding') || wasMeasured(cleanedMetrics, 'sentinel_phrases')} comparisonValue={rawMetrics?.encodingIssues} />
                  <StandaloneMetricRow icon={Shuffle}       label="Back-check Discrepancies" detectorId="backcheck"       value={cleanedMetrics.backcheckIssues}   isMeasured={wasMeasured(cleanedMetrics, 'backcheck')}      comparisonValue={rawMetrics?.backcheckIssues} />
                </div>

                {/* Remaining Issues Summary */}
                {(() => {
                  const m = cleanedMetrics;
                  const wm = (id) => m._measured && m._measured.has(id);
                  const items = [];
                  if (wm('missingness')      && m.missingPct > 2)        items.push(`${m.missingPct.toFixed(1)}% missing values remain`);
                  if (wm('outliers')         && m.outliers > 20)         items.push(`${m.outliers} outliers still detected`);
                  if (wm('duplicates')       && m.duplicates > 0)        items.push(`${m.duplicates} duplicate values present`);
                  if (wm('duration')         && m.durationIssues > 5)    items.push(`${m.durationIssues} duration issues remain`);
                  if (wm('survey_flow')      && m.surveyFlowIssues > 0)  items.push(`${m.surveyFlowIssues} survey-flow violations remain`);
                  if (wm('geolocation')      && m.geolocationIssues > 0) items.push(`${m.geolocationIssues} GPS issues remain`);
                  if (wm('text_clustering')  && m.similarPairs > 5)      items.push(`${m.similarPairs} similar response pairs remain`);
                  if (wm('sentinel_phrases') && m.sentinelFlags > 0)     items.push(`${m.sentinelFlags} sentinel-phrase / short responses remain`);
                  if (wm('gated_fields')     && m.gatedFlags > 0)        items.push(`${m.gatedFlags} gated-field contradictions remain`);
                  if (wm('probe_depth')      && m.probeFlags > 5)        items.push(`${m.probeFlags} low-specificity responses flagged`);
                  if (wm('straight_lining')  && m.straightLining > 0)    items.push(`${m.straightLining} straight-lining respondents remain`);
                  if (wm('encoding')         && m.encodingIssues > 0)    items.push(`${m.encodingIssues} encoding (mojibake) issues remain`);
                  if (items.length === 0) {
                    // No remaining issues among MEASURED detectors — show green banner
                    return (
                      <div style={{
                        marginTop: '20px',
                        padding: '12px 14px',
                        backgroundColor: isDarkMode ? 'rgba(16, 185, 129, 0.08)' : 'rgba(16, 185, 129, 0.06)',
                        border: `0.5px solid ${isDarkMode ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.15)'}`,
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        color: '#10B981',
                        fontSize: '12px'
                      }}>
                        <CheckCircle size={16} />
                        <div>
                          <strong style={{ fontWeight: 500 }}>Excellent!</strong> All measured quality issues have been addressed.
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div style={{
                      marginTop: '20px',
                      padding: '12px 14px',
                      backgroundColor: isDarkMode ? 'rgba(217, 119, 6, 0.08)' : 'rgba(217, 119, 6, 0.06)',
                      borderRadius: '6px',
                      border: `0.5px solid ${isDarkMode ? 'rgba(217, 119, 6, 0.25)' : 'rgba(217, 119, 6, 0.2)'}`
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', color: '#D97706', fontSize: '12px' }}>
                        <AlertTriangle size={13} />
                        <strong style={{ fontWeight: 500, letterSpacing: '-0.01em' }}>Remaining Issues</strong>
                      </div>
                      <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '12px', color: isDarkMode ? '#a8a49a' : '#555550' }}>
                        {items.map((it, i) => <li key={i}>{it}</li>)}
                      </ul>
                    </div>
                  );
                })()}
              </>
            ) : (
              <div style={{ 
                textAlign: 'center', 
                padding: '48px 20px',
                color: isDarkMode ? '#6B7280' : '#9CA3AF'
              }}>
                <AlertTriangle size={32} style={{ marginBottom: '14px', opacity: 0.5 }} />
                <p style={{ marginBottom: '6px', fontSize: '13px', fontWeight: 500, color: isDarkMode ? '#a8a49a' : '#6B7280' }}>Quality analysis not run yet</p>
                <p style={{ fontSize: '12px', opacity: 0.75 }}>
                  Click "Run Quality Analysis" button above to analyze cleaned data
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Info Note */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        padding: '12px 14px',
        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
        border: `0.5px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.06)'}`,
        borderRadius: '6px',
        color: isDarkMode ? '#8a867d' : '#6B7280',
        fontSize: '12px',
        marginTop: '20px',
        lineHeight: 1.5,
      }}>
        <Info size={13} style={{ flexShrink: 0, marginTop: '3px', opacity: 0.7 }} />
        <div>
          <strong>About this comparison:</strong> Quality metrics are recalculated on the cleaned dataset 
          to show actual improvements. Outlier detection uses the <em>same bounds</em> computed during 
          raw data analysis, ensuring an apples-to-apples comparison. If issues persist after cleaning, 
          consider refining your cleaning script or investigating the underlying data collection process.
        </div>
      </div>
    </div>
  );
};

export default ComparisonDashboard;